import { modals } from '@mantine/modals';
import type { UnhandledInvoice } from 'modules/AtRisk/api/use-load-unhandled-client';
import posthog from 'posthog-js';
import { firstBy } from 'remeda';
import { captureEvent } from 'shared/analytics/capture-event';
import type { SendToThirdPartyVariables } from 'shared/hooks/use-send-to-third-party';
import { useSendToThirdParty } from 'shared/hooks/use-send-to-third-party';

export function useThirdPartySend(
  invoices: Array<UnhandledInvoice>,
  extraInvoiceIds: Array<string>,
) {
  const { sendToThirdParty, isSendToThirdPartyLoading } = useSendToThirdParty();

  const invoiceIds = [...invoices.map(invoice => invoice.id), ...extraInvoiceIds];
  const mainInvoiceId =
    firstBy(invoices, invoice => invoice.dueDate.valueOf())?.id ?? extraInvoiceIds[0];
  const additionalInvoiceIds = invoiceIds.filter(id => id !== mainInvoiceId).map(Number);

  const trackAndClose = () => {
    captureEvent(posthog, 'collection:invoices_treated', {
      action_type: 'third_party',
      invoices_treated_count: invoiceIds.length,
    });
    modals.closeAll();
  };

  const send = (thirdPartyCompanyId: string, extraData?: Partial<SendToThirdPartyVariables>) => {
    if (!mainInvoiceId) return;

    sendToThirdParty(
      {
        id: mainInvoiceId,
        data: {
          third_party_company_id: thirdPartyCompanyId,
          additional_invoices: additionalInvoiceIds,
          ...extraData,
        },
      },
      { onSuccess: trackAndClose },
    );
  };

  return {
    invoiceIds,
    mainInvoiceId,
    additionalInvoiceIds,
    isSendToThirdPartyLoading,
    send,
    trackAndClose,
  };
}
